import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef
} from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { DialogData } from 'app/core/dtos/dialog-data';
import { ImportExcelDto } from 'app/core/dtos/excel/import-excel';
import { ExcelService } from 'app/core/services/excel/excel.service';
import Swal from 'app/shared/utils/swal.util';
import { LucideModule } from '../../modules/lucide.module';
import { DialogButtonComponent } from '../dialog-button/dialog-button.component';

const EXTENSAO_ACEITA = '.xlsx';
const TITULO_PADRAO = 'Importar Planilha';
const DESCRICAO_PADRAO = 'Selecione a planilha e confira os dados antes de confirmar a importação.';

@Component({
  selector: 'app-excel-dialog',
  standalone: true,
  imports: [
    LucideModule,
    MatDialogClose,
    MatDialogContent,
    DialogButtonComponent
  ],
  templateUrl: './excel-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './excel-dialog.component.css'
})
export class ExcelDialogComponent {
  private readonly excelService = inject(ExcelService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly extensaoAceita = EXTENSAO_ACEITA;
  readonly titulo: string;
  readonly descricao = DESCRICAO_PADRAO;

  previa: ImportExcelDto | null = null;
  nomeArquivo = '';
  arrastando = false;
  lendo = false;

  get temErros(): boolean {
    return (this.previa?.erros.length ?? 0) > 0;
  }

  get temLinhas(): boolean {
    return (this.previa?.linhas.length ?? 0) > 0;
  }

  constructor(
    public readonly dialogRef: MatDialogRef<ExcelDialogComponent, ImportExcelDto>,
    @Inject(MAT_DIALOG_DATA) public readonly dialogData: DialogData<null>
  ) {
    this.titulo = dialogData?.action || TITULO_PADRAO;
  }

  aoSelecionarArquivo(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const arquivo = input.files?.[0] ?? null;

    input.value = '';
    this.processarArquivo(arquivo);
  }

  aoArrastar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = true;
  }

  aoSairDoArraste(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = false;
  }

  aoSoltar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando = false;
    this.processarArquivo(evento.dataTransfer?.files?.[0] ?? null);
  }

  confirmar(): void {
    if (!this.previa) {
      return;
    }

    this.dialogRef.close(this.previa);
  }

  private processarArquivo(arquivo: File | null): void {
    if (!arquivo || this.lendo) {
      return;
    }

    if (!arquivo.name.toLowerCase().endsWith(EXTENSAO_ACEITA)) {
      Swal.fire({
        icon: 'warning',
        title: 'Arquivo inválido',
        text: `Selecione uma planilha no formato ${EXTENSAO_ACEITA}.`
      });
      return;
    }

    this.nomeArquivo = arquivo.name;
    this.previa = null;
    this.lendo = true;

    this.excelService.ler(arquivo)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.lendo = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe({
        next: (resposta) => {
          this.previa = resposta.data ?? null;
        },
        error: () => {
          this.nomeArquivo = '';
        }
      });
  }
}
